/**
 * NOS-162: embedded on-page donation form (research §C.3 — Feeding America
 * pattern). Preset amount tiles with org-authored impact-equivalency labels,
 * optional custom amount, trust line. Payment runs through the existing
 * DonationModal flow (one-time gifts) — this block adds placement, not a new
 * payment path. Recurring/monthly requires subscription infrastructure and is
 * deliberately out of scope here.
 */
import { useState } from "react"
import { DonationModal } from "@/components/DonationModal"
import { textFromRichHtml, type BlockComponentProps } from "./shared"

interface Tier {
  amount?: number
  impact_label?: string
}

export function DonationFormBlock({ content, org, inBuilder, resolved }: BlockComponentProps) {
  const heading = (content?.heading as string) ?? ""
  const body = textFromRichHtml(content?.body)
  const tiers = ((content?.tiers as Tier[]) ?? []).filter(
    (t) => typeof t.amount === "number" && t.amount! > 0
  )
  const allowCustom = (content?.allow_custom as boolean) ?? true
  const trustText = (content?.trust_text as string) ?? ""
  const ctaText = (content?.cta_text as string) ?? "Donate"

  const [selected, setSelected] = useState<number | null>(tiers[0]?.amount ?? null)
  const [custom, setCustom] = useState("")
  const [modalOpen, setModalOpen] = useState(false)

  const customAmount = Number.parseFloat(custom)
  const effectiveAmount =
    custom.trim() !== "" && Number.isFinite(customAmount) && customAmount > 0
      ? customAmount
      : selected

  const sectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-12"} ${resolved.cornerClass} ${resolved.alignClass}`.trim()
    : "px-4 py-12 @sm:px-6 @md:px-8"

  const selectedLabel = tiers.find((t) => t.amount === selected)?.impact_label

  return (
    <section id="donate" className={sectionClass} style={resolved.sectionStyle}>
      <div
        className="mx-auto max-w-xl rounded-xl border p-6 @sm:p-8 text-center"
        style={{
          background: "var(--nos-color-surface, #ffffff)",
          boxShadow: "var(--nos-elevation-shadow, none)",
        }}
      >
        {heading && <h2 className="text-2xl font-bold tracking-tight @sm:text-3xl">{heading}</h2>}
        {body && (
          <p className="mt-2 text-sm leading-relaxed @sm:text-base" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
            {body}
          </p>
        )}

        {tiers.length > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-2" role="group" aria-label="Donation amount">
            {tiers.slice(0, 6).map((tier) => {
              const active = custom.trim() === "" && selected === tier.amount
              return (
                <button
                  key={tier.amount}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setSelected(tier.amount!)
                    setCustom("")
                  }}
                  className="rounded-lg border px-2 py-3 text-lg font-bold transition-colors"
                  style={
                    active
                      ? {
                          background: "var(--nos-color-primary, #3b82f6)",
                          borderColor: "var(--nos-color-primary, #3b82f6)",
                          color: "var(--nos-color-text-on-primary, #ffffff)",
                        }
                      : undefined
                  }
                >
                  ${tier.amount}
                </button>
              )
            })}
          </div>
        )}

        {/* Impact equivalency for the selected tier — the research-backed conversion lever */}
        {selectedLabel && custom.trim() === "" && (
          <p className="mt-3 text-sm font-medium" style={{ color: "var(--nos-color-primary, #3b82f6)" }}>
            ${selected} — {selectedLabel}
          </p>
        )}

        {allowCustom && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="text-lg font-semibold" aria-hidden="true">
              $
            </span>
            <input
              type="number"
              min={1}
              inputMode="decimal"
              placeholder="Other amount"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              aria-label="Custom donation amount in dollars"
              className="w-40 rounded-lg border px-3 py-2 text-center"
            />
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            if (!inBuilder && effectiveAmount && org?.id) setModalOpen(true)
          }}
          disabled={!effectiveAmount}
          className="mt-6 w-full rounded-lg px-8 py-3 text-lg font-semibold shadow-lg transition-all hover:scale-105 hover:opacity-90 disabled:opacity-50 disabled:hover:scale-100"
          style={{
            background: "var(--nos-color-primary, #3b82f6)",
            color: "var(--nos-color-text-on-primary, #ffffff)",
          }}
        >
          {ctaText}
          {effectiveAmount ? ` $${effectiveAmount}` : ""}
        </button>

        {trustText && (
          <p className="mt-3 text-xs" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
            {trustText}
          </p>
        )}
      </div>

      {!inBuilder && org?.id && modalOpen && effectiveAmount && (
        <DonationModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          orgId={org.id}
          orgName={org.name ?? undefined}
          initialAmountCents={Math.round(effectiveAmount * 100)}
        />
      )}
    </section>
  )
}
